import type { Block, ExerciseId, SteadyKey } from "./types";

/**
 * The Steady programme's five sessions as templates (ADR-052), and the yoga and breathing extra
 * every programme can start (ADR-067): fixed, reviewable blocks whose slots name a move and the
 * pattern it trains, filled per person by `programme.ts::fillTemplate`.
 * A flag the person carries swaps a slot's move for an easier one or drops it, and says why; the
 * shape of the session — its blocks, rounds and rests — never changes.
 *
 * Source: docs/research/2026-10-05/move-library-older-adults.md §3 (the templates as blocks, their
 * doses for weeks 1–2, 3–4 and 5+, the work-to-rest) and §3.1 (how they change per condition).
 * Every template begins seated, groups its moves by position, and ends sitting: never standing still
 * after effort, which is when blood pools and BP tablets make a person dizzy. Where a seated move
 * is followed by a standing one, the rest between them is a "Stand up slowly" rest, and every rest
 * is taken sitting — which is also the falls safeguard.
 *
 * A slot's dose is the move's own beginner · intermediate · established dose (`moves.ts`) unless
 * the report gives the template a different one, which is written here.
 */

/** What a slot trains: the filler keeps the pattern when it swaps a move. */
export type SlotPattern =
  | "warm"
  | "knee"
  | "hip"
  | "calf"
  | "push"
  | "pull"
  | "arms"
  | "balance"
  | "cardio"
  | "walk"
  | "joints"
  | "stretch"
  | "breath";

export interface Slot {
  move: ExerciseId;
  pattern: SlotPattern;
  /** the template's own dose, when the report gives one other than the move's */
  r?: [number, number, number];
  t?: [number, number, number];
  /** the move at the Steady level (a failed test, a fall, unsteady): the report's "semi-tandem →
   *  tandem" slot is the tandem stand, and the staggered stand until the tests say otherwise */
  steady?: ExerciseId;
  /** the move before this one is done sitting: the rest before it is "Stand up slowly" */
  standUp?: true;
  /** a fixed cue for this slot in this template ("hands allowed") */
  cue?: string;
}

export interface TemplateBlock extends Omit<Block, "items"> {
  items: Slot[];
}

export interface Template {
  key: SteadyKey;
  name: string;
  /** the session's one line, as `Session.how` */
  how: string;
  /** the report's length in minutes, low–high, which the tests hold the filled session to */
  mins: [number, number];
  blocks: TemplateBlock[];
}

/** The shared seated warm-up: five minutes, the same at the start of every template. */
export const WARM60: TemplateBlock = {
  title: "Warm-up",
  rounds: [1, 1, 1],
  rest: [5, 5, 5],
  items: [
    { move: "neckturn", pattern: "warm" },
    { move: "shroll", pattern: "warm" },
    { move: "ankle", pattern: "warm", r: [10, 10, 10] },
    { move: "trunkturn", pattern: "warm", r: [5, 5, 5] },
    { move: "backext", pattern: "warm", r: [5, 5, 5] },
    { move: "seatmarch", pattern: "warm", t: [60, 60, 90] },
  ],
};

/** The shared cool-down: a stretch at the wall, then sitting, ending on the breath. */
export const COOL60: TemplateBlock = {
  title: "Cool-down",
  rounds: [1, 1, 1],
  rest: [5, 5, 5],
  items: [
    { move: "calfstretch", pattern: "stretch" },
    { move: "hamchair", pattern: "stretch" },
    { move: "breathing", pattern: "breath", t: [60, 90, 120], cue: "sitting" },
  ],
};

/** SB — Strength & balance, at the chair and the counter (25–30 min). Otago's dose: about 30
 *  minutes three times a week. Work to rest about 1:1, every rest seated. */
const SB: Template = {
  key: "SB",
  name: "Strength & balance",
  how: "Slow on the way down; hold on until the move is easy.",
  mins: [20, 30],
  blocks: [
    WARM60,
    {
      title: "Legs",
      rounds: [1, 2, 2],
      rest: [45, 40, 30],
      restRound: 90,
      items: [
        { move: "kneeext", pattern: "knee", r: [8, 10, 10] },
        { move: "sitstand", pattern: "knee", r: [5, 8, 10] },
        { move: "sideleg", pattern: "hip", r: [5, 8, 10], standUp: true },
        { move: "kneecurl", pattern: "knee", r: [5, 8, 10] },
        { move: "calfraise", pattern: "calf", r: [10, 12, 15] },
        { move: "toeraise", pattern: "calf", r: [10, 12, 15] },
      ],
    },
    {
      // Seated first, the wall last: the standing move leads into the balance block standing.
      title: "Arms",
      rounds: [1, 2, 2],
      rest: [30, 30, 30],
      restRound: 60,
      items: [
        { move: "armcurl", pattern: "arms", r: [8, 10, 12] },
        { move: "row", pattern: "pull", r: [8, 10, 12] },
        { move: "wallpush", pattern: "push", r: [5, 8, 10], standUp: true },
      ],
    },
    {
      title: "Balance",
      rounds: [1, 1, 2],
      rest: [20, 20, 20],
      restRound: 60,
      items: [
        { move: "tandemstand", pattern: "balance", t: [10, 15, 20], steady: "semitandem" },
        { move: "onelegstand", pattern: "balance", t: [5, 10, 15] },
        { move: "sidewalk", pattern: "balance", r: [10, 10, 10] },
        { move: "tandemwalk", pattern: "balance", r: [5, 10, 10] },
        { move: "backwalk", pattern: "balance", r: [10, 10, 10] },
      ],
    },
    COOL60,
  ],
};

/** GC — Gentle cardio & mobility, standing at the counter (20–25 min). "Breathing harder but
 *  able to talk." Work to rest 1:1 in weeks 1–2, 2:1 from week 3, 3:1 from week 5. No jumping,
 *  no fast changes of direction. */
const GC: Template = {
  key: "GC",
  name: "Gentle cardio & mobility",
  how: "Breathing harder but able to talk.",
  mins: [18, 25],
  blocks: [
    WARM60,
    {
      title: "Move",
      rounds: [2, 3, 3],
      rest: [30, 20, 20],
      restRound: 60,
      items: [
        { move: "march", pattern: "cardio", t: [30, 45, 60], standUp: true },
        { move: "steptouch", pattern: "cardio", t: [30, 45, 60] },
        { move: "sidewalk", pattern: "balance", r: [10, 10, 15] },
        { move: "heeldig", pattern: "cardio", t: [30, 45, 60] },
        { move: "seatpunch", pattern: "cardio", t: [30, 45, 60] },
        { move: "armraise", pattern: "arms", r: [8, 10, 10] },
      ],
    },
    {
      title: "Joints",
      rounds: [1, 1, 1],
      rest: [10, 10, 10],
      standUp: true,
      items: [
        { move: "wallslide", pattern: "joints", r: [5, 8, 10] },
        { move: "weightshift", pattern: "balance", r: [5, 8, 8] },
        { move: "tadasana", pattern: "balance", r: [5, 8, 8] },
        { move: "standhipflex", pattern: "stretch", t: [20, 30, 30] },
      ],
    },
    COOL60,
  ],
};

/** CH — the Chair session (15–20 min): everything sitting, nothing should hurt. The bad day, the
 *  75+ start and the first fortnight after a long gap. */
const CH: Template = {
  key: "CH",
  name: "Chair session",
  how: "Everything sitting; nothing should hurt.",
  mins: [12, 20],
  blocks: [
    {
      ...WARM60,
      items: WARM60.items.map((s) => (s.move === "seatmarch" ? { ...s, t: [30, 45, 60] as [number, number, number] } : s)),
    },
    {
      title: "Seated strength",
      rounds: [1, 2, 2],
      rest: [30, 30, 30],
      restRound: 60,
      items: [
        { move: "kneeext", pattern: "knee", r: [5, 8, 10] },
        { move: "heeldig", pattern: "cardio", t: [30, 30, 45] },
        { move: "armcurl", pattern: "arms", r: [5, 8, 10] },
        { move: "row", pattern: "pull", r: [5, 8, 10] },
        { move: "press", pattern: "push", r: [5, 6, 8] },
        { move: "towelsqueeze", pattern: "arms", t: [10, 15, 20] },
        { move: "sitstand", pattern: "knee", r: [3, 5, 5], cue: "hands allowed" },
      ],
    },
    {
      title: "Seated stretch & breath",
      rounds: [1, 1, 1],
      rest: [5, 5, 5],
      items: [
        { move: "chintuck", pattern: "joints", r: [5, 5, 8] },
        { move: "sidebendchair", pattern: "stretch", r: [3, 3, 3] },
        { move: "hamchair", pattern: "stretch", t: [20, 30, 30] },
        { move: "seatfig4", pattern: "stretch", t: [20, 20, 30] },
        { move: "anulom", pattern: "breath", t: [60, 90, 120] },
        { move: "bhramari", pattern: "breath", r: [5, 5, 7] },
      ],
    },
  ],
};

/** MJ — Morning joints (10 min, every day), in bed and then on the edge of the bed. */
const MJ: Template = {
  key: "MJ",
  name: "Morning joints",
  how: "In bed, then sitting on its edge. Sit for 30 seconds before you stand.",
  mins: [5, 10],
  blocks: [
    {
      title: "In bed",
      rounds: [1, 1, 1],
      rest: [5, 5, 5],
      items: [
        { move: "ankle", pattern: "joints", r: [10, 10, 10] },
        { move: "quadset", pattern: "knee", r: [10, 10, 10] },
        { move: "heelslide", pattern: "hip", r: [6, 8, 10] },
        { move: "pelvictilt", pattern: "joints", r: [8, 10, 10] },
        { move: "kneechest", pattern: "stretch", t: [20, 30, 30] },
        { move: "bridge", pattern: "hip", r: [5, 8, 10] },
      ],
    },
    {
      title: "Sitting up",
      rounds: [1, 1, 1],
      rest: [5, 5, 5],
      items: [
        { move: "neckturn", pattern: "joints" },
        { move: "shroll", pattern: "joints" },
        { move: "trunkturn", pattern: "joints", r: [5, 5, 5] },
        { move: "backext", pattern: "joints", r: [5, 5, 5] },
      ],
    },
  ],
};

/** WK — Walk day: five minutes at the door, the walk (10, then 20, then 30 minutes; Otago's 30,
 *  split into tens if one go is too long), five minutes of stretching after it, sitting last. */
const WK: Template = {
  key: "WK",
  name: "Walk day",
  how: "A walk where you can talk but not sing, with five minutes either side.",
  mins: [15, 45],
  blocks: [
    {
      title: "Before the walk",
      rounds: [1, 1, 1],
      rest: [5, 5, 5],
      items: [
        { move: "ankle", pattern: "warm", r: [10, 10, 10] },
        { move: "calfraise", pattern: "calf", r: [10, 10, 12], standUp: true },
        { move: "march", pattern: "cardio", t: [60, 60, 60] },
        { move: "weightshift", pattern: "balance", r: [5, 5, 5] },
        { move: "sidewalk", pattern: "balance", r: [10, 10, 10] },
      ],
    },
    {
      title: "Walk",
      rounds: [1, 1, 1],
      rest: [5, 5, 5],
      items: [{ move: "walk", pattern: "walk" }],
    },
    {
      title: "After the walk",
      rounds: [1, 1, 1],
      rest: [5, 5, 5],
      items: [
        { move: "calfstretch", pattern: "stretch" },
        { move: "standhipflex", pattern: "stretch" },
        { move: "hamchair", pattern: "stretch" },
        { move: "breathing", pattern: "breath", t: [60, 60, 60], cue: "sitting" },
      ],
    },
  ],
};

/** YB — Morning yoga & breathing (15–22 min), for everyone and never scheduled: an extra a person
 *  starts from Train, on a rest day or any morning (ADR-067). A seated sun salutation, not the full
 *  Surya Namaskar — that one goes to the floor and puts the head below the heart. Kapalbhati is a
 *  flagged move rather than a rule: high BP, BP tablets and pregnancy swap it down to its `easier`,
 *  which the block already has, so the fill leaves it out and says why. */
const YB: Template = {
  key: "YB",
  name: "Morning yoga & breathing",
  how: "Slow and seated. Breathe through the nose; nothing should strain.",
  mins: [15, 22],
  blocks: [
    {
      title: "Settle",
      rounds: [1, 1, 1],
      rest: [10, 10, 10],
      items: [
        { move: "breathing", pattern: "breath", t: [60, 60, 90], cue: "sitting" },
        { move: "neckturn", pattern: "warm", r: [5, 5, 5] },
        { move: "shroll", pattern: "warm" },
      ],
    },
    {
      // One breath a movement, so a round is slow: the rests are breaths taken sitting.
      title: "Seated sun salutation",
      rounds: [2, 3, 3],
      rest: [10, 10, 10],
      restRound: 45,
      items: [
        { move: "chairtadasana", pattern: "stretch", r: [5, 5, 5] },
        { move: "foldchair", pattern: "stretch", r: [5, 5, 5] },
        { move: "backext", pattern: "stretch", r: [5, 5, 5] },
        { move: "catcowchair", pattern: "stretch", r: [6, 6, 6] },
        { move: "sidebendchair", pattern: "stretch", r: [3, 3, 3] },
        { move: "trunkturn", pattern: "stretch", r: [5, 5, 5] },
      ],
    },
    {
      title: "Pranayama",
      rounds: [1, 1, 1],
      rest: [30, 30, 30],
      items: [
        { move: "anulom", pattern: "breath", t: [120, 150, 180] },
        { move: "bhramari", pattern: "breath", r: [5, 7, 9] },
        { move: "kapalbhati", pattern: "breath", t: [30, 45, 60] },
      ],
    },
    {
      title: "Close",
      rounds: [1, 1, 1],
      rest: [5, 5, 5],
      items: [{ move: "breathing", pattern: "breath", t: [60, 90, 120], cue: "sitting" }],
    },
  ],
};

export const TEMPLATES: Readonly<Record<SteadyKey, Template>> = { SB, GC, CH, MJ, WK, YB };

export const STEADY_KEYS: readonly SteadyKey[] = ["SB", "GC", "CH", "MJ", "WK", "YB"];

export function isSteadyKey(key: unknown): key is SteadyKey {
  return typeof key === "string" && (STEADY_KEYS as readonly string[]).includes(key);
}

/** The balance block from SB, which a balance goal adds to the walk day as its pre-walk (report §4). */
export const SB_BALANCE: TemplateBlock = SB.blocks[3];

/** SB's isometric finisher for controlled high BP (report §3.1): a shallow wall sit, building
 *  towards 4 × 2 minutes, one to two minutes' seated rest between, breathing throughout. */
export const BP_FINISHER: TemplateBlock = {
  title: "Wall sit",
  rounds: [2, 3, 4],
  rest: [60, 60, 60],
  restRound: 90,
  items: [{ move: "wallsit", pattern: "knee", cue: "shallow; count out loud, never hold the breath" }],
};
